import React from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { RiTeamLine } from "react-icons/ri";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { createWorshipBibleTeamUser } from "../../components/helpers/RegistrationHelpers";
import withNavigationHook from "../../hooks/withNavigationHook";

class RegisterMulti extends React.Component {
  state = {
    teamName: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    privacyPolicy: false,
    couponCode: "",
    submitting: false,
    error: "",
    showPassword: false,
    showConfirmPassword: false,
  };

  componentDidMount() {
    const storedCustomer = sessionStorage.getItem("customer");
    const customer = storedCustomer ? JSON.parse(storedCustomer) : null;
    if (customer?.user?.accountRole) {
      this.props.navigation("/your-profile/team");
    }
  }

  togglePasswordVisibility = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };

  toggleConfirmPasswordVisibility = () => {
    this.setState((prev) => ({ showConfirmPassword: !prev.showConfirmPassword }));
  };

  handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    this.setState({ [name]: type === "checkbox" ? checked : value, error: "" });
  };

  validate = () => {
    const { teamName, firstName, lastName, email, password, confirmPassword, privacyPolicy } = this.state;

    if (!teamName.trim() || !firstName.trim() || !lastName.trim() || !email.trim() || !password || !confirmPassword) {
      return "Please complete all required fields.";
    }

    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return "Please enter a valid email address.";
    }

    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }

    if (password !== confirmPassword) {
      return "Confirmed password does not match password.";
    }

    if (!privacyPolicy) {
      return "Please confirm you agree to the Privacy Policy and Terms & Conditions.";
    }

    return "";
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = this.validate();

    if (validationError) {
      this.setState({ error: validationError });
      return;
    }

    this.setState({ submitting: true, error: "" });

    try {
      const response = await createWorshipBibleTeamUser(
        this.props,
        this.state.firstName.trim(),
        this.state.lastName.trim(),
        this.state.email.trim(),
        this.state.password,
        this.state.teamName.trim(),
        this.state.couponCode.trim()
      );

      sessionStorage.setItem("selectedAccountType", "team");
      sessionStorage.setItem("selectedPlan", "year");
      sessionStorage.setItem("clientSecret", response.clientSecret);
      sessionStorage.setItem("intentType", response.intentType);
      if (this.state.couponCode.trim()) {
        sessionStorage.setItem("couponCode", this.state.couponCode.trim());
      }

      this.props.navigation("/register/billing");
    } catch (error) {
      this.setState({
        submitting: false,
        error: error?.message || "Unable to complete registration. Please try again.",
      });
    }
  };

  render() {
    const { submitting, error } = this.state;

    return (
      <>
        <div className="position-relative">
          <section className="section section-lg section-shaped pb-250">
            <div className="shape shape-style-1 shape-default">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </div>
            <Container className="py-lg-md d-flex">
              <div className="col px-0">
                <Row>
                  <Col lg="8">
                    <h1 className="display-3 text-white">
                      Register your group <span>plan services together</span>
                    </h1>
                  </Col>
                </Row>
              </div>
            </Container>
            <div className="separator separator-bottom separator-skew">
              <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" version="1.1" viewBox="0 0 2560 100" x="0" y="0">
                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
              </svg>
            </div>
          </section>
        </div>

        <section className="section section-lg pt-lg-0 mt--200">
          <Container>
            <Row className="justify-content-center">
              <Col lg="10">
                <Row className="row-grid">
                  <Col lg="4">
                    <Card className="shadow border-0 mb-4">
                      <CardBody className="py-5">
                        <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                          <RiTeamLine color="#5e72e4" size="36" /> Group
                        </h2>
                        <h3 className="mt-4 mb-2">&pound;300/year</h3>
                        <p className="description mb-2">Up to 10 members</p>
                        <p className="description mb-0">
                          Collaborative access for small churches and music teams planning worship around scripture.
                        </p>
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="8">
                    <Card className="shadow border-0">
                      <CardBody className="py-5">
                        <h2 style={{ fontSize: "1rem" }} className="text-primary text-uppercase">
                          Create your group account
                        </h2>
                        {error && <Alert color="warning" className="mt-4">{error}</Alert>}
                        <Form className="mt-4" onSubmit={this.handleSubmit}>
                          <FormGroup>
                            <Label for="teamName">Group name</Label>
                            <Input id="teamName" name="teamName" value={this.state.teamName} onChange={this.handleChange} />
                          </FormGroup>
                          <Row>
                            <Col md="6">
                              <FormGroup>
                                <Label for="firstName">First name</Label>
                                <Input id="firstName" name="firstName" value={this.state.firstName} onChange={this.handleChange} />
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label for="lastName">Last name</Label>
                                <Input id="lastName" name="lastName" value={this.state.lastName} onChange={this.handleChange} />
                              </FormGroup>
                            </Col>
                          </Row>
                          <FormGroup>
                            <Label for="email">Email address</Label>
                            <Input id="email" name="email" type="email" value={this.state.email} onChange={this.handleChange} />
                          </FormGroup>
                          <Row>
                            <Col md="6">
                              <FormGroup>
                                <Label for="password">Password</Label>
                                <InputGroup>
                                  <Input id="password" name="password" type={this.state.showPassword ? "text" : "password"} value={this.state.password} onChange={this.handleChange} />
                                  <InputGroupText onClick={this.togglePasswordVisibility} style={{ cursor: "pointer" }}>
                                    {this.state.showPassword ? <FaEyeSlash /> : <FaEye />}
                                  </InputGroupText>
                                </InputGroup>
                              </FormGroup>
                            </Col>
                            <Col md="6">
                              <FormGroup>
                                <Label for="confirmPassword">Confirm password</Label>
                                <InputGroup>
                                  <Input id="confirmPassword" name="confirmPassword" type={this.state.showConfirmPassword ? "text" : "password"} value={this.state.confirmPassword} onChange={this.handleChange} />
                                  <InputGroupText onClick={this.toggleConfirmPasswordVisibility} style={{ cursor: "pointer" }}>
                                    {this.state.showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                  </InputGroupText>
                                </InputGroup>
                              </FormGroup>
                            </Col>
                          </Row>
                          <FormGroup>
                            <Label for="couponCode">Promotion code</Label>
                            <Input id="couponCode" name="couponCode" value={this.state.couponCode} onChange={this.handleChange} />
                          </FormGroup>
                          <FormGroup check className="mb-4">
                            <Label check>
                              <Input name="privacyPolicy" type="checkbox" checked={this.state.privacyPolicy} onChange={this.handleChange} />{" "}
                              I agree to the Privacy Policy and Terms & Conditions
                            </Label>
                          </FormGroup>
                          <Button color="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner size="sm" /> Creating group...</> : "Continue to billing"}
                          </Button>
                        </Form>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </section>
      </>
    );
  }
}

export default withNavigationHook(RegisterMulti);
